
import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import MatrixContainer from "@/components/MatrixContainer.vue"; // TODO: encapsulate AlarmClip inside Camera
import DeviceController from "@/components/DeviceController.vue";
import {
	CameraType,
	ClipType,
	MatrixCamera,
	MatrixClip,
	MatrixStoredFile,
	MatrixContents,
	MatrixContentItem,
	StoredFileType
} from "@/store/site-monitor-cameras/types";
import { EventDetails, EventRecord, EventRegion } from "@/store/site-monitor/types";
import { FeaturesList } from "@/store/types";
import { get } from "lodash";
import api from "@/services/api.service";

import { MessageBroadcastChannel } from "@/utils/messageBroadcastChannel";
import { MatrixEvents } from "@/types/MatrixEvents";

const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const Sessions = namespace("sessions");
const Tours = namespace("tours");
const EventQueue = namespace("eventqueue");

interface Matrix {
	eventID: number;
	matrixColumns: number;
	matrixRows: number;
	cells: MatrixCell[];
}

interface MatrixCell {
	eventID: number;
	cellIndex: number;
	contents: string;
}

interface MatrixCellContentParsedCamera {
	deviceId: number;
}

interface MatrixCellContentParsedClip {
	fileIdentifier: string;
	eventRecordID: number;
}

interface MatrixCellContentParsedStoredFile {
	fileName: string;
	fileLocation: string;
	fileType: StoredFileType;
	index: number;
}

interface MatrixCellContentParsed {
	clip: MatrixCellContentParsedClip | null;
	camera: MatrixCellContentParsedCamera | null;
	storedFile: MatrixCellContentParsedStoredFile | null;
}

@Component({
	components: {
		"matrix-container": MatrixContainer,
		"device-controller": DeviceController
	}
})
export default class Cameras extends Vue {
	$refs!: {
		expandedCamera: any;
	};

	@Getter("getFeaturesList") private featuresList: FeaturesList;

	@SMCameras.State private waitingForEvent: boolean;

	@SMCameras.Getter("getLayouts") private layouts!: number[][];
	@SMCameras.Getter("getLayoutIndex") private getLayoutIndex!: number;
	@SMCameras.Getter("getDeviceControllerCameras") private cameras!: CameraType[];
	@SMCameras.Getter("getDeviceControllerClips") private clips!: ClipType[];
	@SMCameras.Getter private getMatrixPushContents: (index: number) => MatrixContentItem;
	@SMCameras.Getter private getMatrixContents: (index: number) => MatrixContentItem;
	@SMCameras.Getter private getAllMatrixContents: MatrixContents;

	@SMCameras.Action private saveMatrix: (payload: any) => Promise<void>;

	@SMCameras.Mutation private clearMatrixContents: () => void;
	@SMCameras.Mutation private setMediaMatrixIsNew: (isNew: boolean) => void;
	@SMCameras.Mutation private setFetchingMatrix: (fetchingMatrix: boolean) => void;
	@SMCameras.Mutation private setPushContents: ({ index, newContents }) => void;
	@SMCameras.Mutation private setLayoutIndex: (index: number) => void;
	@SMCameras.Mutation private setShownAuditClips: (clips: string[]) => void;
	@SMCameras.Mutation private setDeviceControllerCameras: (cameras: MatrixCamera[]) => void;
	@SMCameras.Mutation private setDeviceControllerClips: (clips: MatrixClip[]) => void;
	@SMCameras.Mutation private setDeviceControllerOutputs: (outputs: any[]) => void;
	@SMCameras.Mutation private clearDeviceControllerStoredFiles: () => void;
	@SMCameras.Mutation private setLinkRecord: (record: EventRecord) => void;
	@SMCameras.Mutation private setWaitingForEvent: (waiting: boolean) => void;
	@SMCameras.Mutation private setAwaitingCamera: (camera: MatrixCamera) => void;
	@SMCameras.Mutation private setAwaitingClip: (clip: MatrixClip) => void;
	@SMCameras.Mutation private setAwaitingStoredFile: (storedFile: MatrixStoredFile) => void;

	@SiteMonitor.Getter("getEventDetails") private eventDetails: EventDetails;
	@SiteMonitor.Getter("getIsController") private isController: boolean;

	@SiteMonitor.Action private createAuditRecord: (payload: any) => Promise<void>;
	@SiteMonitor.Action private resetRegion: () => Promise<void>;
	@SiteMonitor.Action private updateEventRegion: ({ regionId, regionUrl }) => Promise<void>;

	@SiteMonitor.Mutation private setEventDetails: (details: EventDetails) => void;

	@Sessions.Action private updateSession: ({ resourceId, eventId }) => Promise<void>;
	@Sessions.Action private clearSessions: () => Promise<void>;

	@Tours.Getter private tourInProgress: boolean;

	@Inject("matrixChannel") private mediaMatrixChannel: MessageBroadcastChannel;

	private updateTimer: NodeJS.Timeout | null = null;
	private loadedMatrix: boolean = false;
	private showMenu: boolean = true;
	private expandedItem: HTMLElement | null = null;
	private expandParent: HTMLElement | null = null;
	private itemExpanded: boolean = false;

	/**
	 * Gets the state of the clear matrix feature flag.
	 * @returns true if the flag is enabled, otherwise false.
	 */
	private get isClearEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "Clear"]);
	}

	/**
	 * Gets the state of the hide matrix menu feature flag.
	 * @returns true if the flag is enabled, otherwise false.
	 */
	private get featureHideMenuEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "HideMenu"]);
	}

	/**
	 * Gets the selected layout index.
	 * @returns the layout index number.
	 */
	private get layoutIndex() {
		return this.getLayoutIndex;
	}

	/**
	 * Sets the selected layout index.
	 * @param index the new layout index.
	 */
	private set layoutIndex(index: number) {
		this.setLayoutIndex(index);
	}

	/**
	 * Gets the current matrix layout.
	 * @returns the current matrix layout (a two-length array).
	 */
	public get layout(): number[] {
		return this.layouts[this.layoutIndex];
	}

	/**
	 * Gets the current layout's horizontal column count.
	 * @returns the number of columns in the current layout.
	 */
	public get layoutHorizontal() {
		return this.layouts[this.layoutIndex][0];
	}

	/**
	 * Gets the current layout's vertical row count.
	 * @returns the number of rows in the current layout.
	 */
	public get layoutVertical() {
		return this.layouts[this.layoutIndex][1];
	}

	/**
	 * Computes the style to use for the matrix grid.
	 * @returns a style object.
	 */
	public get cameraRowStyle() {
		return {
			display: "grid",
			"grid-template-rows": 1 / this.layoutHorizontal,
			"grid-template-columns": 1 / this.layoutVertical
		};
	}

	/**
	 * Gets the current event id.
	 * @returns the current event id, or 0 if there is no event.
	 */
	public get eventId() {
		return this.eventDetails ? this.eventDetails.eventID : 0;
	}

	/**
	 * Created lifecycle hook, clears the matrix contents, and informs the UI we need
	 * event details.
	 */
	private async created() {
		this.clearMatrixContents();
		this.setMediaMatrixIsNew(false);

		// Add our message listeners for comms from the site monitor.
		this.mediaMatrixChannel.addListener(MatrixEvents.Reset, this.onMatrixReset);
		this.mediaMatrixChannel.addListener(MatrixEvents.SetEventRegion, this.onSetEventRegion);
		this.mediaMatrixChannel.addListener(MatrixEvents.SetEventDetails, this.onSetEventDetails);

		// Post our ready event
		await this.mediaMatrixChannel.postMessage(MatrixEvents.Ready);
	}

	/**
	 * Mounted lifecycle hook, starts the matrix polling for any changes made to the
	 * matrix by other users.
	 */
	public mounted() {
		this.updateTimer = setInterval(() => this.update(), 2000);
	}

	/**
	 * Destroyed lifecycle hook, clears the update timer.
	 */
	public destroyed() {
		if (this.updateTimer != null) {
			clearInterval(this.updateTimer);
		}
		this.setLinkRecord(null);
	}

	/**
	 * Updates the list of audit clips to match any changes in the media matrix contents.
	 */
	@Watch("getAllMatrixContents", { deep: true })
	private onMatrixUpdated(matrixContents: any) {
		let clipsLoaded: Array<string> = [];

		for (let i in matrixContents) {
			if (matrixContents[i].clip !== undefined) {
				clipsLoaded.push(matrixContents[i].clip.clip.FileIdentifier);
			}
		}

		this.setShownAuditClips(clipsLoaded);
	}

	/**
	 * Updates the matrix when the users control over the event changes.
	 */
	@Watch("isController")
	private async onIsControllerChanged(value: boolean, oldValue: boolean) {
		if (value && this.eventId > 0 && this.loadedMatrix) {
			await this.updateMatrix(this.eventId);
		}
	}

	/**
	 * Handler for when the SM requests the MM is reset. Clears the region,
	 * the event details, and sets us back to waiting for an event.
	 */
	private async onMatrixReset() {
		// Clear the event details first, as this will prevent Sessions from attempting to
		// refresh after we've switched regions.
		this.setEventDetails(null);
		await this.resetRegion();

		this.setWaitingForEvent(true);
		this.clearMatrixContents();

		this.setDeviceControllerCameras([]);
		this.setDeviceControllerClips([]);
		this.setDeviceControllerOutputs([]);
		this.clearDeviceControllerStoredFiles();

		this.clearAwaitingContent();
		this.setLinkRecord(null);
		this.loadedMatrix = false;

		// Notify SM that we're ready to receive an event again.
		await this.mediaMatrixChannel.postMessage(MatrixEvents.Ready);
	}

	/**
	 * Handler for when the SM requests the MM updates it's event region.
	 * Refreshes the site tree.
	 */
	private async onSetEventRegion(region: EventRegion) {
		await this.updateEventRegion(region);

		await this.mediaMatrixChannel.postMessage(MatrixEvents.SetEventRegionComplete);
	}

	/**
	 * Handler for when the SM requests the MM updates it's event details.
	 * Loads the event and displays the matrix.
	 */
	private async onSetEventDetails(details: EventDetails) {
		this.setEventDetails(details);
		this.setWaitingForEvent(!details);

		await this.loadEvent(details?.eventID);
		await this.mediaMatrixChannel.postMessage(MatrixEvents.SetEventDetailsComplete);
	}

	/**
	 * Sets up the matrix for an event, refreshing audit/device service sessions.
	 */
	private async loadEvent(eventId: number) {
		this.clearMatrixContents();
		this.clearSessions();

		// Update both if our audit and device server sessions
		await this.updateSession({
			resourceId: "2",
			eventId: eventId
		});

		await this.updateSession({
			resourceId: "3",
			eventId: eventId
		});

		await this.updateMatrix(eventId);
	}

	/**
	 * Updates the matrix to the latest for a given event id from the data service. If no matrix is
	 * returned, we assume that this event hasn't had a matrix before, and create a new empty grid.
	 * @param eventId the event to get the matrix for.
	 */
	private async updateMatrix(eventId: number): Promise<void> {
		this.setMediaMatrixIsNew(false);
		this.setFetchingMatrix(true);
		const matrix = await api.matrixGet(eventId);

		if (matrix) {
			if (this.isController) {
				// Check if any cell has non-empty data, if so, consider new.
				const allBlank = !matrix.cells.some(
					cell => cell.contents !== "{}" && cell.contents !== '{"clip":null,"camera":null}'
				);
				const isNew = matrix.cells.length == 0 || allBlank;

				// Mark for default setup if has no cells or all cells are empty.
				this.setMediaMatrixIsNew(isNew);
				if (!isNew) {
					console.log("Got existing matrix ");
					this.setMatrix(matrix);
				} else {
					console.log("Empty matrix");
				}
			} else {
				this.setMediaMatrixIsNew(false);
				this.setMatrix(matrix);
			}
		} else {
			console.log("New matrix for event " + eventId);

			if (this.isController) {
				let layout = this.layout;

				// Save new matrix
				await api.matrixSave({
					eventID: this.eventId,
					matrixColumns: layout[0],
					matrixRows: layout[1],
					cells: []
				});

				this.setMediaMatrixIsNew(true);
			} else {
				console.log("Not the controller - matrix will not be saved");
			}
			this.loadedMatrix = true;
		}
	}

	/**
	 * Applies matrix data from the data service to the UI. Applies any changes to the grid,
	 * including cameras, clips, and stored files.
	 * @param matrix the matrix data returned from the data service.
	 */
	private setMatrix(matrix: Matrix) {
		console.log("Setting up matrix ... ");

		const layoutIndex = this.layouts.findIndex(
			layout => layout[0] == matrix.matrixRows && layout[1] == matrix.matrixColumns
		);

		if (layoutIndex > -1) {
			this.layoutIndex = layoutIndex;
		}

		/**
		 * Populates a cell with a camera, if that camera isn't already within the cell.
		 */
		const setCamera = (
			newCamera: MatrixCellContentParsedCamera,
			currentCamera: MatrixCamera,
			cellIndex: number
		) => {
			if (!currentCamera || currentCamera.objectID != newCamera.deviceId) {
				// Try to find the matching camera from the list of matrix cameras we've already loaded.
				// If we can't find a match, we'll still try to play the camera.
				const matchingCamera = this.cameras.find(camera => camera.objectID == newCamera.deviceId);
				this.setPushContents({
					index: cellIndex,
					newContents: {
						camera: matchingCamera || {
							objectID: newCamera.deviceId,
							title: "",
							index: cellIndex
						}
					}
				});
			}
		};

		/**
		 * Populates a cell with a clip, if that clip isn't already within the cell.
		 */
		const setClip = (newClip: MatrixCellContentParsedClip, currentClip: MatrixClip, cellIndex: number) => {
			if (!currentClip || currentClip.clip.FileIdentifier != newClip.fileIdentifier) {
				this.setPushContents({
					index: cellIndex,
					newContents: {
						clip: {
							eventRecordID: newClip.eventRecordID,
							clip: {
								FileIdentifier: newClip.fileIdentifier,
								index: cellIndex
							}
						}
					}
				});
			}
		};

		/**
		 * Populates a cell with a stored file, if that file isn't already within the cell.
		 */
		const setStoredFile = (
			newStoredFile: MatrixCellContentParsedStoredFile,
			currentStoredFile: MatrixStoredFile,
			cellIndex: number
		) => {
			if (!currentStoredFile || currentStoredFile.fileLocation != newStoredFile.fileLocation) {
				this.setPushContents({
					index: cellIndex,
					newContents: {
						storedFile: {
							...newStoredFile,
							index: cellIndex
						}
					}
				});
			}
		};

		/**
		 * Updates a matrix cell with new contents.
		 */
		const setMatrixCell = (cell: MatrixCell) => {
			// Get the current content of this matrix cell
			const currentContents = this.getMatrixContents(cell.cellIndex);

			// Parse the new contents of the cell
			const newContents = JSON.parse(cell.contents) as MatrixCellContentParsed;
			if (!newContents) {
				return;
			}

			if (newContents.camera) {
				setCamera(newContents.camera, currentContents?.camera, cell.cellIndex);
			} else if (newContents.clip) {
				setClip(newContents.clip, currentContents?.clip, cell.cellIndex);
			} else if (newContents.storedFile) {
				setStoredFile(newContents.storedFile, currentContents?.storedFile, cell.cellIndex);
			} else if (currentContents?.camera || currentContents?.clip || currentContents?.storedFile) {
				// This cell currently has a camera/clip/stored file, but it's new contents indicate
				// it is empty - this means the controller of the event has cleared the cell.
				this.setPushContents({
					index: cell.cellIndex,
					newContents: {}
				});
			}
		};

		if (matrix.cells != null) {
			matrix.cells.forEach(setMatrixCell);
		}
	}

	/**
	 * Fetches the latest matrix data from the data service and applies it to the media matrix.
	 */
	private async update() {
		if (!this.isController && this.eventId != undefined && this.eventId > 0) {
			let matrix = await api.matrixGet(this.eventId);
			if (matrix) {
				this.setMatrix(matrix);
			}
		}
	}

	/**
	 * Handler for when the user selects a new layout. Saves the updated matrix to the db.
	 */
	private layoutChanged() {
		this.setLayoutIndex(this.layoutIndex);

		if (this.eventId > 0) {
			let [matrixColumns, matrixRows] = this.layout;
			this.saveMatrix({
				eventID: this.eventId,
				matrixColumns,
				matrixRows
			});
		}
	}

	/**
	 * Gets the style to use for each camera column within the grid.
	 * @param layoutIndex the current layout index.
	 */
	private cameraColStyle(layoutIndex: number) {
		let hindex = Math.floor(layoutIndex / this.layoutHorizontal) + 1;
		let vindex = (layoutIndex % this.layoutHorizontal) + 1;

		let style = {
			margin: "2px",
			"grid-column-start": vindex,
			"grid-column-end": vindex,
			"grid-row-start": hindex,
			"grid-row-end": hindex
		};

		return style;
	}

	/**
	 * Handles expanding to full-screen in a grid cell.
	 * @param matrixElement the HTMLElement containing the grid cell.
	 */
	private expand(matrixElement: HTMLElement) {
		if (this.itemExpanded) {
			this.itemExpanded = false;
			this.expandParent.appendChild(matrixElement);
			this.expandParent = null;
			this.expandedItem = null;
		} else {
			this.expandedItem = matrixElement;
			this.itemExpanded = true;
			this.expandParent = matrixElement.parentElement;
			this.$refs.expandedCamera.appendChild(matrixElement);
		}
	}

	/**
	 * Handles contracting (exiting full-screen) in a grid cell.
	 * @param matrixElement the HTMLElement containing the grid cell.
	 */
	private contract(matrixElement: HTMLElement) {
		if (this.itemExpanded && this.expandedItem == matrixElement) {
			this.itemExpanded = false;
			if (this.expandParent != null) {
				this.expandParent.appendChild(matrixElement);
				this.expandParent = null;
			}
		}
	}

	/**
	 * Handler for when the user presses the escape key. Clears all the 'awaiting'
	 * cameras/clips/stored files (ie, when the user clicks a camera in the sidebar).
	 */
	private clearAwaitingContent() {
		this.setAwaitingCamera(null);
		this.setAwaitingClip(null);
		this.setAwaitingStoredFile(null);
	}

	/**
	 * Handler for when the user clicks the Clear button. Clears all cells in the
	 * matrix, and audits the change.
	 */
	private async clearAllMatrixCells() {
		const matrixContent = this.getAllMatrixContents;
		const matrixEntries = Object.entries(matrixContent);

		matrixEntries.forEach(cell => {
			this.setPushContents({
				index: cell[0],
				newContents: {}
			});
		});

		const auditRecord = {
			eventId: this.eventId,
			eventRecordTypeId: 4,
			details: "Media Matrix Cleared"
		};

		await this.createAuditRecord(auditRecord);
	}
}
